import { Schema } from "effect";
import { HttpApiEndpoint, HttpApiError, HttpApiGroup, HttpApiSchema } from "effect/http-api";
import { OpenApiClass } from "../openapi-class.js";
import { UUID } from "../uuid.js";

export const PLUTO_WRITING_INSTRUCTIONS_MAX_LENGTH = 8_000;
export const PLUTO_OUTREACH_OBJECTIVE_MAX_LENGTH = 2_000;
export const PLUTO_OUTREACH_FINDING_MAX_LENGTH = 2_000;
export const PLUTO_OUTREACH_MAX_FINDINGS = 12;
/** Tones label an angle in a narrow tab, so only a word or two ever fits. */
export const PLUTO_OUTREACH_TONE_MAX_LENGTH = 32;
/** One line under the draft naming the trigger it was built on. */
export const PLUTO_OUTREACH_RATIONALE_MAX_LENGTH = 280;
/** The recommended draft, plus at most two genuinely different angles. */
export const PLUTO_OUTREACH_MAX_VARIANTS = 3;
export const PLUTO_REPLY_PROMPT_MAX_LENGTH = 4_000;
export const PLUTO_REPLY_MAX_CONTEXTS = 8;
export const PLUTO_REPLY_TEXT_MAX_LENGTH = 8_000;
export const PLUTO_CONVERSATION_TITLE_MAX_LENGTH = 200;

const WritingInstructionsSchema = Schema.Trim.pipe(
  Schema.check(Schema.isNonEmpty({ message: "Writing instructions cannot be empty" })),
  Schema.check(
    Schema.isMaxLength(PLUTO_WRITING_INSTRUCTIONS_MAX_LENGTH, {
      message: `Writing instructions cannot exceed ${String(PLUTO_WRITING_INSTRUCTIONS_MAX_LENGTH)} characters`,
    })
  )
);

/**
 * User-provided guidance for Pluto's email and SMS drafts.
 */
export class PlutoWritingPreferencesPayload extends OpenApiClass<PlutoWritingPreferencesPayload>(
  "PlutoWritingPreferencesPayload"
)({
  instructions: WritingInstructionsSchema.annotate({
    description:
      "User-scoped guidance for tone, phrasing, structure, length, language, calls to action, and sign-off",
  }),
}) {}

/**
 * The authenticated user's current Pluto writing preferences.
 *
 * Version zero represents the deterministic default when no preference has
 * been saved.
 */
export class PlutoWritingPreferencesResponse extends OpenApiClass<PlutoWritingPreferencesResponse>(
  "PlutoWritingPreferencesResponse"
)({
  instructions: Schema.String,
  version: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  updatedAt: Schema.OptionFromNullOr(Schema.String),
}) {}

const OutreachTextSchema = (label: string, maxLength: number) =>
  Schema.Trim.pipe(
    Schema.check(Schema.isNonEmpty({ message: `${label} cannot be empty` })),
    Schema.check(
      Schema.isMaxLength(maxLength, {
        message: `${label} cannot exceed ${String(maxLength)} characters`,
      })
    )
  );

/**
 * One copy-ready outreach draft. SMS variants never include a subject.
 *
 * The tone names the angle the draft takes, in the language it was written in,
 * so alternates can be told apart by their strategy rather than by number.
 */
export class PlutoOutreachDraftVariant extends OpenApiClass<PlutoOutreachDraftVariant>(
  "PlutoOutreachDraftVariant"
)({
  tone: Schema.String.pipe(
    Schema.check(Schema.isNonEmpty()),
    Schema.check(Schema.isMaxLength(PLUTO_OUTREACH_TONE_MAX_LENGTH))
  ),
  subject: Schema.OptionFromNullOr(Schema.String.pipe(Schema.check(Schema.isMaxLength(200)))),
  body: Schema.String.pipe(
    Schema.check(Schema.isNonEmpty()),
    Schema.check(Schema.isMaxLength(5_000))
  ),
}) {}

/**
 * The draft Pluto recommends, followed by any alternate angles.
 *
 * The first variant is the one to send. Pluto adds an alternate only when a
 * genuinely different strategy exists, so a lone variant means it found one
 * good way in rather than three interchangeable rewordings.
 */
export class PlutoOutreachDraft extends OpenApiClass<PlutoOutreachDraft>("PlutoOutreachDraft")({
  channel: Schema.Literals(["email", "sms"]),
  orgnr: Schema.String,
  companyName: Schema.String,
  variants: Schema.NonEmptyArray(PlutoOutreachDraftVariant).pipe(
    Schema.check(Schema.isMaxLength(PLUTO_OUTREACH_MAX_VARIANTS))
  ),
  /** Which trigger the recommended draft was built on, so the user can redirect. */
  rationale: Schema.String.pipe(
    Schema.check(Schema.isMaxLength(PLUTO_OUTREACH_RATIONALE_MAX_LENGTH))
  ),
  writingInstructionsVersion: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  usedFindingCount: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
  /**
   * How many approved case pages Pluto read while writing. It measures the
   * research, not the draft: a page can be read and then judged a poor fit, and
   * a case named in the objective can be written about without reading a page,
   * so this is not a record of what the draft ended up citing.
   */
  usedCaseSourceCount: Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0))),
}) {}

/**
 * One user-visible page context chip attached to a Pluto reply request.
 */
export class PlutoReplyContextPayload extends OpenApiClass<PlutoReplyContextPayload>(
  "PlutoReplyContextPayload"
)({
  id: OutreachTextSchema("Context id", 64),
  label: OutreachTextSchema("Context label", 80),
  value: OutreachTextSchema("Context value", 200),
}) {}

/**
 * A general Pluto research reply for the current page and prompt.
 *
 * Omitting `conversationId` starts a new conversation; the server mints the id
 * and returns it so the next turn can continue the same thread.
 */
export class PlutoReplyPayload extends OpenApiClass<PlutoReplyPayload>("PlutoReplyPayload")({
  prompt: OutreachTextSchema("Prompt", PLUTO_REPLY_PROMPT_MAX_LENGTH),
  contexts: Schema.Array(PlutoReplyContextPayload).pipe(
    Schema.check(Schema.isMaxLength(PLUTO_REPLY_MAX_CONTEXTS))
  ),
  conversationId: Schema.OptionFromOptional(UUID),
}) {}

const PlutoReplyTextSchema = Schema.Trim.pipe(
  Schema.check(Schema.isNonEmpty({ message: "Reply cannot be empty" })),
  Schema.check(
    Schema.isMaxLength(PLUTO_REPLY_TEXT_MAX_LENGTH, {
      message: `Reply cannot exceed ${String(PLUTO_REPLY_TEXT_MAX_LENGTH)} characters`,
    })
  )
);

/**
 * Pluto's assistant text for a single user turn, plus the conversation it belongs to.
 */
export class PlutoReplyResponse extends OpenApiClass<PlutoReplyResponse>("PlutoReplyResponse")({
  text: PlutoReplyTextSchema,
  conversationId: UUID,
  conversationTitle: Schema.String,
  usedToolNames: Schema.Array(Schema.String),
  /**
   * Outreach the turn wrote, when the user asked for some. It accompanies the
   * text rather than replacing it: `text` introduces the drafts and this is the
   * copy itself, which the user edits and sends.
   */
  draft: Schema.OptionFromNullOr(PlutoOutreachDraft),
}) {}

/**
 * A tool Pluto has just started running, sent while the answer is still being
 * worked out so a client can say what is happening instead of only that
 * something is.
 */
export class PlutoReplyToolEvent extends Schema.Class<PlutoReplyToolEvent>("PlutoReplyToolEvent")({
  type: Schema.Literal("tool"),
  name: Schema.String,
}) {}

/**
 * A fragment of the answer as the model produces it.
 *
 * Advisory only: `done` remains the authoritative text. A turn makes several
 * model calls and keeps the prose of only the last one, and a retry replays the
 * turn from its first call, so text that has already been shown can turn out to
 * belong to a discarded attempt. `generation` increments whenever a new model
 * call begins, and a client that sees it change must drop what it has collected
 * so far rather than appending to it. The final text is also trimmed and capped
 * after generation, which no sequence of fragments can anticipate.
 */
export class PlutoReplyDeltaEvent extends Schema.Class<PlutoReplyDeltaEvent>(
  "PlutoReplyDeltaEvent"
)({
  type: Schema.Literal("delta"),
  generation: Schema.Int,
  text: Schema.String,
  /** Reasoning rather than answer text, to be shown as such or not at all. */
  reasoning: Schema.Boolean,
}) {}

/** The finished answer, identical to what the unstreamed endpoint returns. */
export class PlutoReplyDoneEvent extends Schema.Class<PlutoReplyDoneEvent>("PlutoReplyDoneEvent")({
  type: Schema.Literal("done"),
  reply: PlutoReplyResponse,
}) {}

/**
 * A turn that failed after the response had already begun. The status line is
 * long gone by then, so the reason has to travel in the body.
 */
export class PlutoReplyErrorEvent extends Schema.Class<PlutoReplyErrorEvent>(
  "PlutoReplyErrorEvent"
)({
  type: Schema.Literal("error"),
  message: Schema.String,
}) {}

/** One newline-delimited JSON line of a streamed reply. */
export const PlutoReplyStreamEvent = Schema.Union([
  PlutoReplyToolEvent,
  PlutoReplyDeltaEvent,
  PlutoReplyDoneEvent,
  PlutoReplyErrorEvent,
]);

export type PlutoReplyStreamEvent = Schema.Schema.Type<typeof PlutoReplyStreamEvent>;

export const PLUTO_REPLY_STREAM_CONTENT_TYPE = "application/x-ndjson";

/**
 * One of the authenticated user's Pluto conversations, newest activity first.
 */
export class PlutoConversationSummary extends OpenApiClass<PlutoConversationSummary>(
  "PlutoConversationSummary"
)({
  id: UUID,
  title: Schema.String,
  createdAt: Schema.String,
  updatedAt: Schema.String,
}) {}

/**
 * One stored turn of a Pluto conversation, as replayed into the transcript.
 *
 * A turn that delivered an outreach draft carries it in `draft` so the reopened
 * transcript shows the same card the user saw when it was written. `text` holds
 * the same draft as prose, so a turn stays readable without it.
 */
export class PlutoConversationMessage extends OpenApiClass<PlutoConversationMessage>(
  "PlutoConversationMessage"
)({
  id: UUID,
  role: Schema.Literals(["user", "assistant"]),
  text: Schema.String,
  toolNames: Schema.Array(Schema.String),
  draft: Schema.OptionFromNullOr(PlutoOutreachDraft),
  /** The user stopped this answer, so it stops mid-thought rather than concluding. */
  stopped: Schema.Boolean,
  createdAt: Schema.String,
}) {}

/**
 * A Pluto conversation reopened from history.
 */
export class PlutoConversationHistoryResponse extends OpenApiClass<PlutoConversationHistoryResponse>(
  "PlutoConversationHistoryResponse"
)({
  conversationId: UUID,
  title: Schema.String,
  messages: Schema.Array(PlutoConversationMessage),
}) {}

/**
 * The authenticated user's Pluto conversation list.
 */
export class PlutoConversationListResponse extends OpenApiClass<PlutoConversationListResponse>(
  "PlutoConversationListResponse"
)({
  conversations: Schema.Array(PlutoConversationSummary),
}) {}

const conversationIdParam = UUID;

export const PlutoV3Group = HttpApiGroup.make("plutoV3")
  .add(
    HttpApiEndpoint.get("getPlutoWritingPreferences", "/pluto/writing-preferences", {
      success: PlutoWritingPreferencesResponse,
      error: HttpApiError.InternalServerErrorNoContent,
    })
  )
  .add(
    HttpApiEndpoint.put("putPlutoWritingPreferences", "/pluto/writing-preferences", {
      payload: PlutoWritingPreferencesPayload,
      success: PlutoWritingPreferencesResponse,
      error: [HttpApiError.BadRequestNoContent, HttpApiError.InternalServerErrorNoContent],
    })
  )
  .add(
    HttpApiEndpoint.delete("clearPlutoWritingPreferences", "/pluto/writing-preferences", {
      success: PlutoWritingPreferencesResponse,
      error: HttpApiError.InternalServerErrorNoContent,
    })
  )
  .add(
    HttpApiEndpoint.post("createPlutoReply", "/pluto/replies", {
      payload: PlutoReplyPayload,
      success: PlutoReplyResponse,
      error: [
        HttpApiError.BadRequestNoContent,
        HttpApiError.NotFoundNoContent,
        HttpApiError.ServiceUnavailableNoContent,
        HttpApiError.InternalServerErrorNoContent,
      ],
    })
  )
  .add(
    // Same turn as createPlutoReply, answered as a stream of PlutoReplyStreamEvent
    // lines so the tool being run can be reported before the answer exists.
    HttpApiEndpoint.post("createPlutoReplyStream", "/pluto/replies/stream", {
      payload: PlutoReplyPayload,
      success: Schema.Uint8Array.pipe(
        HttpApiSchema.asUint8Array({ contentType: PLUTO_REPLY_STREAM_CONTENT_TYPE })
      ),
      error: [
        HttpApiError.BadRequestNoContent,
        HttpApiError.NotFoundNoContent,
        HttpApiError.InternalServerErrorNoContent,
      ],
    })
  )
  .add(
    HttpApiEndpoint.get("getPlutoConversations", "/pluto/conversations", {
      success: PlutoConversationListResponse,
      error: HttpApiError.InternalServerErrorNoContent,
    })
  )
  .add(
    HttpApiEndpoint.get(
      "getPlutoConversationMessages",
      "/pluto/conversations/:conversationId/messages",
      {
        params: { conversationId: conversationIdParam },
        success: PlutoConversationHistoryResponse,
        error: [
          HttpApiError.BadRequestNoContent,
          HttpApiError.NotFoundNoContent,
          HttpApiError.InternalServerErrorNoContent,
        ],
      }
    )
  );
